import { useQuery } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";
import {
  buildCasinoKpiMoneyTypeParam,
  parseCasinoKpiFilterMetadata,
  type CasinoKpiFilterMetadata,
  type CasinoKpiFilterValues,
} from "./casinoKpiFilters";

export type CasinoV2KpiCardsFilters = CasinoKpiFilterValues & {
  start_date: string;
  end_date: string;
};

export type CasinoV2KpiCard = {
  key: string;
  value: number;
  percentage: number;
  rate: boolean;
};

type RawKpiEntry = {
  value: number;
  percent_change: number | null;
};

export type CasinoV2KpiCardsResult = CasinoKpiFilterMetadata & {
  cards: CasinoV2KpiCard[];
};

const RATE_KEYS = new Set(["margin_pct"]);

// Fixed render order for the 6 headline Casino V2 KPI cards. The hook only
// returns keys present in the API response, so backend changes (added /
// removed metrics) flow through without a code change here.
export const CARD_ORDER = [
  "turnover",
  "ggr",
  "margin_pct",
  "total_rounds",
  "avg_bet_amount",
  "total_users",
];

const fetchCasinoV2KpiCards = async (
  filters: CasinoV2KpiCardsFilters,
): Promise<CasinoV2KpiCardsResult> => {
  // getPrivateInstance throws if the stored token JSON is corrupt; fall back
  // to an unauthenticated instance so a malformed token degrades to a 401
  // rather than a thrown parse error (preserves the prior try/catch behavior).
  let instance;
  try {
    instance = getPrivateInstance();
  } catch {
    instance = createPrivateAdminPanelInstance(undefined);
  }

  const { money_type, ...dateFilters } = filters;
  const response = await instance.get(API_ROUTES.CASINO_KPI_CARDS, {
    params: {
      ...dateFilters,
      ...buildCasinoKpiMoneyTypeParam(money_type),
    },
  });

  const raw = response?.data?.data;
  const cards =
    raw && typeof raw === "object"
      ? CARD_ORDER.filter((key) => key in raw).map((key) => {
          const entry = raw[key] as RawKpiEntry;
          return {
            key,
            value: entry.value ?? 0,
            percentage: entry.percent_change ?? 0,
            rate: RATE_KEYS.has(key),
          };
        })
      : [];
  const metadata = parseCasinoKpiFilterMetadata(response?.data);

  return { cards, ...metadata };
};

export const useGetCasinoV2KpiCards = (filters: CasinoV2KpiCardsFilters) => {
  const { start_date, end_date, money_type } = filters;

  return useQuery<CasinoV2KpiCardsResult, Error>({
    queryKey: [
      "CASINO_V2_KPI_CARDS",
      start_date,
      end_date,
      money_type.join(","),
    ],
    queryFn: () => fetchCasinoV2KpiCards(filters),
    enabled: Boolean(start_date && end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};
